import { ClientOnly } from "@tanstack/react-router";
import { lazy, Suspense, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "@/common/providers/i18n-provider";
import { useIsDarkTheme, useTheme } from "@/common/providers/theme-provider";
import { AppHeader } from "@/components/app-header";
import { apiReferenceConfiguration } from "@/components/api-reference-config";
import { bridgeScalarTheme } from "@/components/api-reference-theme";
import { LocaleLink } from "@/components/locale-link";
import { API_REFERENCES, type ApiReference } from "@/config/api-references";

// Scalar includes a complete API client and its Vue runtime. Keep both out of
// every other route's bundle, and load the library CSS in the same route-only
// chunk instead of making it a global render-blocking stylesheet.
const ScalarReference = lazy(async () => {
  const [scalar] = await Promise.all([
    import("@scalar/api-reference-react"),
    import("@scalar/api-reference-react/style.css"),
  ]);

  return { default: scalar.ApiReferenceReact };
});

function ThemeSyncedScalarReference({ api }: { api: ApiReference }) {
  const { setTheme } = useTheme();
  const isDark = useIsDarkTheme();
  const { locale, t } = useTranslation();
  const productionServer = t("apiReference.productionServer");
  const configuration = useMemo(
    () => apiReferenceConfiguration(isDark, locale, productionServer, api),
    [isDark, locale, productionServer, api],
  );

  // Scalar mounts its Vue app in a passive effect. A layout effect establishes
  // the CMS preference first, so Scalar cannot flash or restore a stale value
  // from its separate `colorMode` localStorage key.
  useLayoutEffect(
    () => bridgeScalarTheme(isDark, setTheme),
    [isDark, setTheme],
  );

  return <ScalarReference configuration={configuration} />;
}

function ReferenceFallback({ api }: { api: ApiReference }) {
  const { t } = useTranslation();
  const reference = API_REFERENCES[api];

  return (
    <div className="mx-auto min-h-[70vh] max-w-3xl px-6 py-20">
      <p className="font-mono text-sm font-medium uppercase tracking-[0.16em] text-primary">
        {t("apiReference.eyebrow")}
      </p>
      <h1 className="mt-3 text-4xl font-semibold tracking-tight">
        {t(reference.titleKey)}
      </h1>
      <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
        {t(reference.descriptionKey)}
      </p>
      <p className="mt-8 text-sm text-muted-foreground" role="status">
        {t("apiReference.loading")}
      </p>
      <a
        className="mt-4 inline-block font-medium text-primary underline-offset-4 hover:underline"
        href={reference.specUrl}
      >
        {t("apiReference.viewSpec")}
      </a>
    </div>
  );
}

export function ApiReferencePage({
  api = "beancount",
}: {
  api?: ApiReference;
}) {
  const { t } = useTranslation();

  return (
    <>
      <AppHeader />
      <main id="main" tabIndex={-1}>
        <nav
          aria-label={t("apiReference.navigation")}
          className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3 sm:px-6"
        >
          {Object.entries(API_REFERENCES).map(([key, reference]) => (
            <LocaleLink
              key={key}
              to={reference.path}
              aria-current={api === key ? "page" : undefined}
              className={
                api === key
                  ? "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                  : "rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              }
            >
              {t(reference.labelKey)}
            </LocaleLink>
          ))}
          <a
            href={API_REFERENCES[api].specUrl}
            className="px-4 py-2 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline sm:ms-auto"
          >
            {t("apiReference.viewSpec")}
          </a>
        </nav>
        <ClientOnly fallback={<ReferenceFallback api={api} />}>
          <Suspense fallback={<ReferenceFallback api={api} />}>
            <ThemeSyncedScalarReference key={api} api={api} />
          </Suspense>
        </ClientOnly>
      </main>
    </>
  );
}

export function PricesApiReferencePage() {
  return <ApiReferencePage api="prices" />;
}
